import { Skeleton, Typography, Paper, useTheme, Box, Tabs, Tab, Stack, Tooltip, IconButton } from '@mui/material';

import Iconify from 'src/components/iconify';

import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'src/context/store';
import { GridFilterModel } from '@mui/x-data-grid';
import { GridSortModel } from '@mui/x-data-grid';
import { useDataQuery, useKpisQuery } from 'src/context/api/data/api';
import { useExportMutation } from 'src/context/api/exports/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import GenericDataGrid from 'src/components/data-grid/generic-data-grid';
import { GridColumnVisibilityModel, useGridApiRef, GridPreferencePanelsValue } from '@mui/x-data-grid-pro';
import { EXPLORE_COLORS, GRID_HEIGHT, GRID_DEFAULT_METRICS, GRID_BAR_TYPES, GRID_COLUMN_ORDER, exploreColumnMinWidths, buildExploreBaseTabs } from './constants';
import ExploreAnalysisChartModal from '../funnel/ExploreAnalysisChartModal';
import 'src/components/data-grid/data-grid.scss';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

export default function ExploreTable() {
  const { t } = useLocales();
  const theme = useTheme();
  const workspace = useCurrentWorkspace();
  const filterStore = useSelector((state: RootState) => state.filters);
  const loadedPlatforms = useSelector((state: RootState) => state.filters.loadedPlatform);
  const mode = useSelector((state: RootState) => state.filters.extraOptions.mode);
  const [value, setValue] = useState(0);
  const [showEvolution, setShowEvolution] = useState(true);
  const [showAnalysisChart, setShowAnalysisChart] = useState(false);
  const gridApiRef = useGridApiRef();
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  // La ClickAwayListener du panneau se ferme sur pointerup/mouseup (avant le click).
  // On mémorise donc l'état d'ouverture au mousedown pour un vrai toggle au click.
  const columnsWasOpenRef = useRef(false);
  const filterWasOpenRef = useRef(false);
  // Anchor for the columns/filter panel popover: set to the clicked button so the panel opens under
  // it (the buttons live in this custom header, outside the grid, so MUI can't anchor on its own).
  const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field:
        value === 3
          ? filterStore?.timestep.toLowerCase().replace('date_day', 'date')
          : 'brand_searches',
      sort: 'desc',
    },
  ]);
  const [filters, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [exportMutation] = useExportMutation();
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({});

  // Funnel-only column order: keep Brand Searches Difference directly after Brand Searches.
  const gridColumnOrder = useMemo(
    () => GRID_COLUMN_ORDER.flatMap((field) =>
      field === 'brand_searches' ? [field, 'brand_searchesDiff'] : [field]
    ),
    []
  );

  const dateField = useMemo(() => {
    switch (filterStore?.timestep?.toLowerCase()) {
      case 'date_day':
        return 'date';

      case 'year_week':
        return 'year_week';

      case 'year_month':
        return 'year_month';

      default:
        return 'date';
    }
  }, [filterStore?.timestep]);

  const isLastYearComparison = useMemo(() => {
    if (!filterStore?.compare || !filterStore?.start || !filterStore?.end ||
      !filterStore?.compareStart || !filterStore?.compareEnd) {
      return false;
    }

    const start = new Date(filterStore.start);
    const end = new Date(filterStore.end);
    const compareStart = new Date(filterStore.compareStart);
    const compareEnd = new Date(filterStore.compareEnd);

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      Number.isNaN(compareStart.getTime()) ||
      Number.isNaN(compareEnd.getTime())
    ) {
      return false;
    }

    const isSameUtcDate = (left: Date, right: Date) =>
      left.getUTCFullYear() === right.getUTCFullYear() &&
      left.getUTCMonth() === right.getUTCMonth() &&
      left.getUTCDate() === right.getUTCDate();

    const shiftUtcDays = (date: Date, days: number) => {
      const shifted = new Date(
        Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate()
        )
      );
      shifted.setUTCDate(shifted.getUTCDate() + days);
      return shifted;
    };

    // Weekly Last Year follows the product rule:
    // current week Monday -> comparison week Monday - 364 days.
    // The picker start itself may be mid-week, so derive the first Monday
    // inside the selected current range before checking compareStart.
    if (filterStore?.timestep?.toLowerCase() === 'year_week') {
      const normalizedStart = new Date(
        Date.UTC(
          start.getUTCFullYear(),
          start.getUTCMonth(),
          start.getUTCDate()
        )
      );

      const startDay = normalizedStart.getUTCDay();
      const daysUntilMonday =
        startDay === 1 ? 0 : startDay === 0 ? 1 : 8 - startDay;

      const firstMonday = shiftUtcDays(normalizedStart, daysUntilMonday);
      const expectedCompareStart = shiftUtcDays(firstMonday, -364);
      const expectedCompareEnd = shiftUtcDays(end, -364);

      return (
        isSameUtcDate(compareStart, expectedCompareStart) &&
        isSameUtcDate(compareEnd, expectedCompareEnd)
      );
    }

    // Daily / Monthly keep the existing calendar-year detection.
    const isPreviousYearDate = (current: Date, compared: Date) =>
      current.getFullYear() - compared.getFullYear() === 1 &&
      current.getMonth() === compared.getMonth() &&
      current.getDate() === compared.getDate();

    return (
      isPreviousYearDate(start, compareStart) &&
      isPreviousYearDate(end, compareEnd)
    );
  }, [
    filterStore?.compare,
    filterStore?.start,
    filterStore?.end,
    filterStore?.compareStart,
    filterStore?.compareEnd,
    filterStore?.timestep,
  ]);

  const dateComparisonLabel = useMemo(() => {
    if (!filterStore?.compare) {
      return '';
    }

    const timestep = filterStore?.timestep?.toLowerCase();

    if (isLastYearComparison) {
      switch (timestep) {
        case 'date_day':
          return 'vs same day previous year';

        case 'year_week':
          return 'vs same week previous year';

        case 'year_month':
          return 'vs same month previous year';

        default:
          return 'vs previous year';
      }
    }

    switch (timestep) {
      case 'date_day':
        return 'vs previous day';

      case 'year_week':
        return 'vs previous week';

      case 'year_month':
        return 'vs previous month';

      default:
        return 'vs previous period';
    }
  }, [
    filterStore?.compare,
    filterStore?.timestep,
    isLastYearComparison,
  ]);

  const tabs = useMemo(() => {
    let baseTabs = buildExploreBaseTabs(
      filterStore?.timestep,
      filterStore?.countries
    );

    // Countries must stay visible even when a single country is selected.
    // buildExploreBaseTabs may omit it in that case, so we add it back explicitly.
    if (!baseTabs.some((tab) => tab.value === 1)) {
      const countryTab = {
        label: 'dashboard.filters.countries',
        value: 1,
        groupby: ['region_name'],
      };

      baseTabs = [
        baseTabs[0],
        countryTab,
        ...baseTabs.slice(1),
      ].filter(Boolean);
    }

    // Platforms must be derived as part of the memoized tabs structure.
    // Do not mutate `tabs` later in an effect: a re-render caused by another filter
    // would recreate baseTabs without Platforms, while loadedPlatforms itself may
    // not change and therefore the old effect would not run again.
    if (
      loadedPlatforms.length > 1 &&
      !baseTabs.some((tab) => tab.value === 8)
    ) {
      baseTabs = [
        ...baseTabs,
        {
          label: 'dashboard.filters.platforms',
          value: 8,
          groupby: ['platform'],
        },
      ];
    }

    return {
      base: baseTabs,
    };
  }, [
    filterStore?.timestep,
    filterStore?.countries,
    loadedPlatforms,
  ]);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);

    // Reset du sort model lors du changement de tab pour éviter les problèmes de validation
    if (newValue === 3) {
      setSortModel([
        { field: filterStore?.timestep.toLowerCase().replace('date_day', 'date'), sort: 'desc' },
      ]);
    } else {
      const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
      setSortModel([{ field: defaultField, sort: 'desc' }]);
    }

    // Reset aussi les filtres si nécessaire
    setFilterModel({
      items: [],
    });
  };

  const { data: kpis, isLoading: isFetchingKPIs, error: kpisError } = useKpisQuery();

  const order_by = useMemo(() => {
    if (!sortModel.length) return [];

    if (value === 3) {
      const sort = sortModel[0]?.sort === 'asc' ? 'asc' : 'desc';

      return [[dateField, sort]] as [string, string][];
    }

    return sortModel
      .map(({ field, sort }) => {
        const compareMatch = field.match(/^(.*?)(Compare|Diff)$/);

        if (compareMatch) {
          const baseField = compareMatch[1].toLowerCase();
          const suffix = compareMatch[2];

          if (suffix === 'Compare') {
            return [`compare.${baseField}`, sort];
          }

          if (suffix === 'Diff') {
            return [`diff.${baseField}`, sort];
          }
        }

        const kpi = kpis?.find((k) => k.id === field);

        if (!kpi) return null;

        return [field, sort];
      })
      .filter((item) => !!item) as [string, string][];
  }, [sortModel, kpis, value, dateField]);

  const groupby = useMemo(() => {
    if (value === 3) {
      return [dateField];
    }

    return (
      tabs[mode as keyof typeof tabs]
        .find((tab) => tab.value === value)?.groupby ?? []
    );
  }, [tabs, value, mode, dateField]);

  const gridPinnedColumns = useMemo(() => {
    if (value === 3) {
      return [dateField];
    }

    return groupby ?? [];
  }, [value, groupby, dateField]);

  // Hover "Filter" shortcut:
  // 0 = Search Terms, 1 = Countries, 2 = Devices, 8 = Platforms.
  // Use groupby[0] so the grid receives the real backend dimension field
  // (search_term / region_name / device_type / platform) instead of duplicating names here.
  const dimensionFilterField = useMemo(() => {
    if (![0, 1, 2, 8].includes(value)) {
      return undefined;
    }

    return groupby?.[0];
  }, [value, groupby]);

  const defaultSortModelForTab = useMemo<GridSortModel>(() => {
    if (value === 3) {
      return [
        {
          field: filterStore?.timestep?.toLowerCase().replace('date_day', 'date') ?? 'date',
          sort: 'desc',
        },
      ];
    }
    const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
    return [{ field: defaultField, sort: 'desc' }];
  }, [value, mode, filterStore?.timestep]);

  const queryOpts = useMemo(
    () => ({
      group_by: groupby,
      comparison: filterStore?.compare ? 'true' : 'false',
      start: filterStore?.start,
      end: filterStore?.end,
      interval: filterStore?.timestep.toLowerCase(),
      page_size: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search_terms: filterStore?.keywords ?? [],
      countries: filterStore?.countries ?? [],
      devices: filterStore?.devices ?? [],
      platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
      start_compare: filterStore?.compare ? filterStore?.compareStart : undefined,
      end_compare: filterStore?.compare ? filterStore?.compareEnd : undefined,
      mode: 'base' as 'base' | 'activations',
      output: 'table' as 'table' | 'charts',
      filters,
      workspace_id: workspace?.id as number,
      extra_options: filterStore?.extraOptions,
      order_by,
      tags: filterStore?.tags?.map((tag: any) => tag.label) ?? [],
    }),
    [
      groupby,
      filterStore?.compare,
      filterStore?.start,
      filterStore?.end,
      filterStore?.timestep,
      paginationModel.pageSize,
      paginationModel.page,
      filterStore?.keywords,
      filterStore?.countries,
      filterStore?.devices,
      filterStore?.platforms,
      filterStore?.compareStart,
      filterStore?.compareEnd,
      filterStore?.extraOptions,
      order_by,
      filterStore?.tags,
    ]
  );

  const {
    data: rawResponse,
    isFetching: isFetchingTableData,
    error: tableDataError,
  } = useDataQuery(queryOpts, {
    skip:
      !filterStore?.timestep ||
      !filterStore?.compareStart ||
      !filterStore?.compareEnd ||
      !kpis?.length ||
      groupby.length === 0,
  });

  // Conserve la dernière réponse chargée. Un changement de filtre/tri/page produit de NOUVEAUX
  // arguments de requête : RTK Query crée alors une nouvelle entrée de cache dont `data` est
  // `undefined` le temps du fetch. Sans rétention, la grille se démonterait (skeleton) à chaque
  // interaction et perdrait son état interne (colonnes ajoutées/retirées). On garde donc l'ancienne
  // réponse affichée sous l'overlay de chargement jusqu'à l'arrivée des nouvelles données.
  const [lastResponse, setLastResponse] = useState<any>(undefined);
  useEffect(() => {
    if (rawResponse) setLastResponse(rawResponse);
  }, [rawResponse]);
  const response = rawResponse ?? lastResponse;
  const { data: tableData, total_rows = 0, query_job_id: queryID } = response ?? {};

  const handleExport = () => {
    const columns = (gridApiRef.current?.getAllColumns?.() ?? []) as {
      field: string;
      headerName?: string;
    }[];
    exportMutation({
      queryId: queryID as string,
      name: `[${workspace?.name}] Traffic details export`,
      columns: Object.keys(visibilityModel).filter(
        (key) => visibilityModel[key as keyof typeof visibilityModel] !== false
      ),
      column_tr: Object.keys(visibilityModel).reduce(
        (acc, key) => {
          acc[key] = columns.find((column) => column.field === key)?.headerName as string;
          return acc;
        },
        {} as Record<string, string>
      ),
      dimensions: groupby,
    })
      .unwrap()
      .then(() => {
        enqueueSnackbar(
          'Export is running in the background, you will be notified when it\'s done.',
          {
            autoHideDuration: 10000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            content: (snackbarId) => (
              <Paper
                elevation={0}
                sx={{
                  width: 560,
                  maxWidth:
                    'calc(100vw - 32px)',
                  minHeight: 52,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '8px 12px 8px 8px',
                  bgcolor: '#FFFFFF',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 8px 16px rgba(145, 158, 171, 0.20), 0px 0px 2px rgba(145, 158, 171, 0.24)',
                }}
              >
                <Box
                  sx={{
                    width: '26px !important',
                    height: '26px !important',
                    flexShrink: 0,
                    padding: 2,
                    display: 'flex',
                    alignItems:
                      'center',
                    justifyContent:
                      'center',
                    borderRadius:
                      '8px',
                    bgcolor:
                      '#FFF8E7',
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 18 18"
                    fill="none"
                    sx={{
                      width: '21px !important',
                      height: '21px !important',
                      flexShrink: 0,
                      animation:
                        'exportToastSpin 1s linear infinite',
                      '@keyframes exportToastSpin': {
                        from: {
                          transform:
                            'rotate(0deg)',
                        },
                        to: {
                          transform:
                            'rotate(360deg)',
                        },
                      },
                    }}
                  >
                    <g clipPath="url(#export-toast-spinner-clip)">
                      <path
                        d="M9 1.5V4.5M9 13.5V16.5M4.5 9H1.5M16.5 9H13.5M14.3088 14.3088L12.1875 12.1875M14.3088 3.74996L12.1875 5.87128M3.69118 14.3088L5.8125 12.1875M3.69118 3.74996L5.8125 5.87128"
                        stroke="#B76E00"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>

                    <defs>
                      <clipPath id="export-toast-spinner-clip">
                        <rect
                          width="18"
                          height="18"
                          fill="white"
                        />
                      </clipPath>
                    </defs>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    flex: 1,
                    color:
                      '#333C47',
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    lineHeight:
                      '22px',
                  }}
                >
                  Export is running in the background, you will be notified when it&apos;s done.
                </Typography>

                <IconButton
                  size="small"
                  onClick={() =>
                    closeSnackbar(
                      snackbarId
                    )
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    flexShrink: 0,
                    color:
                      '#919EAB',
                  }}
                >
                  <Iconify
                    icon="mingcute:close-line"
                    width={16}
                  />
                </IconButton>
              </Paper>
            ),
          }
        );
      })
      .catch(() => {
        enqueueSnackbar('Error exporting data', { variant: 'error' });
      });
  };

  // Tant que le contenu tient sous GRID_HEIGHT, on laisse MUI dimensionner la grille
  // (autoHeight) : elle épouse les lignes au pixel près, sans vide ni « row fantôme ».
  // Au-delà du plafond, on repasse en hauteur fixe + scroll vertical interne (comportement d'origine).
  // L'estimation ne sert qu'à choisir le mode ; une imprécision près du seuil est sans effet visible.
  const fitsWithoutCap = useMemo(() => {
    const HEADER_H = 36; // columnHeaderHeight
    const ROW_H = 44; // rowHeight
    const FOOTER_H = 52; // footer de pagination
    const rowsCount = Array.isArray(tableData) ? tableData.length : 0;
    return HEADER_H + rowsCount * ROW_H + FOOTER_H <= GRID_HEIGHT;
  }, [tableData]);

  return (
    <>
      <Paper
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#212b36' : 'white',
          boxShadow:
            '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
          borderRadius: 2,
          // v7: l'en-tête est peint via ces variables CSS (containerBackground = colonnes
          // normales, pinnedBackground = colonnes dimension épinglées). Styler
          // `.MuiDataGrid-columnHeaders` n'a aucun effet ici.
          '& .MuiDataGrid-root': {
            '--DataGrid-containerBackground': EXPLORE_COLORS.headerBg,
            '--DataGrid-pinnedBackground': EXPLORE_COLORS.headerBg,
          },

          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
            paddingLeft: '12px',
            paddingRight: '12px',
          },

          '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': {
            paddingLeft: '20px',
          },
          '& .MuiDataGrid-cell': {
            color: EXPLORE_COLORS.valueDark,
            fontSize: 14,
            // Séparateurs pleins (les défauts MUI apparaissent pointillés) — #919EAB @ 20%.
            borderBottom: `1px solid ${EXPLORE_COLORS.gridBorder}`,
            borderRight: `1px solid ${EXPLORE_COLORS.gridBorder}`,
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: `1px solid ${EXPLORE_COLORS.gridBorder}`,
          },
          // Hover sur TOUTE la ligne (y compris la colonne search term épinglée, dont le fond blanc
          // !important masque sinon le survol natif — cf. .MuiDataGrid-cell--pinnedLeft du grid partagé).
          '& .MuiDataGrid-row:hover': {
            backgroundColor: EXPLORE_COLORS.rowHover,
          },
          '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: `${EXPLORE_COLORS.rowHover} !important`,
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: EXPLORE_COLORS.footerBg,
            minHeight: '40px',
            height: '40px',
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
              color: EXPLORE_COLORS.textDisabled,
              fontSize: 14,
            },
            '& .MuiToolbar-root': { minHeight: '40px' },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '40px',
            pl: '20px',
            pr: '12px',
            borderBottom: `1px solid ${EXPLORE_COLORS.borderNeutral}`,
          }}
        >
          <Tabs
            TabIndicatorProps={{
              sx: {
                backgroundColor: EXPLORE_COLORS.tabActive,
                height: '2px',
              },
            }}
            value={value}
            onChange={handleChange}
            aria-label={t('dashboard.brandTraffic.basic_tabs_example')}
            sx={{
              minHeight: '40px',
              height: '40px',

              '& .MuiTabs-flexContainer': {
                height: '40px',
                alignItems: 'center',
              },

              '& .MuiTab-root': {
                minHeight: '40px',
                height: '40px',
                minWidth: 'auto',

                display: 'flex',
                padding: '10px 6px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                alignSelf: 'stretch',

                '&:not(:last-of-type)': {
                  marginRight: '24px',
                },

                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                color: EXPLORE_COLORS.textDisabled,
              },

              '& .Mui-selected': {
                color: `${EXPLORE_COLORS.tabActive} !important`,
              },
            }}
          >
            {tabs[mode as keyof typeof tabs].map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={
                  tab.value === 3 ? (
                    <Stack
                      component="span"
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box component="span">
                        {t(tab.label)}
                      </Box>

                      {dateComparisonLabel && (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: '8px',
                            height: '22px',
                            borderRadius: '11px',
                            backgroundColor: 'rgba(139, 61, 255, 0.10)',
                            color: '#8B3DFF',
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '18px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          {dateComparisonLabel}
                        </Box>
                      )}
                    </Stack>
                  ) : (
                    t(tab.label)
                  )
                }
              />
            ))}
          </Tabs>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip
              title={showAnalysisChart ? 'Hide analysis chart' : 'Show analysis chart'}
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => setShowAnalysisChart(true)}
                sx={{
                  display: 'flex',
                  padding: '4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  width: 26,
                  height: 26,
                  minWidth: 26,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(145, 158, 171, 0.20)',
                  '&:hover': {
                    backgroundColor: 'rgba(145, 158, 171, 0.40)',
                  },
                }}
              >
                <Box
                  component="svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  sx={{
                    display: 'block',
                  }}
                >
                  <path
                    d="M15 15V9.75M9 15V7.5M3 15L3 12M10.055 3.77063L13.9313 5.22425M8.09908 4.05069L3.90017 7.19987M15.7955 4.8295C16.2348 5.26884 16.2348 5.98116 15.7955 6.4205C15.3562 6.85984 14.6438 6.85984 14.2045 6.4205C13.7652 5.98116 13.7652 5.26884 14.2045 4.8295C14.6438 4.39016 15.3562 4.39016 15.7955 4.8295ZM3.7955 7.0795C4.23483 7.51884 4.23483 8.23116 3.7955 8.6705C3.35616 9.10984 2.64384 9.10984 2.2045 8.6705C1.76517 8.23116 1.76517 7.51884 2.2045 7.0795C2.64384 6.64016 3.35616 6.64016 3.7955 7.0795ZM9.7955 2.5795C10.2348 3.01884 10.2348 3.73116 9.7955 4.1705C9.35616 4.60983 8.64384 4.60983 8.2045 4.1705C7.76517 3.73116 7.76517 3.01884 8.2045 2.5795C8.64384 2.14017 9.35616 2.14017 9.7955 2.5795Z"
                    stroke="#5E6D7B"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>
              </IconButton>
            </Tooltip>

            {filterStore?.compare && (
              <Tooltip
                title={
                  showEvolution
                    ? 'Showing % evolution — click for absolute difference'
                    : 'Showing absolute difference — click for % evolution'
                }
                placement="top"
              >
                <Box
                  onClick={() => setShowEvolution((v) => !v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    px: '6px',
                    py: '4px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    bgcolor: showEvolution ? 'rgba(145,158,171,0.08)' : 'rgba(145,158,171,0.08)',
                    color: showEvolution ? '#1e88e5' : '#1e88e5',
                  }}
                >
                  <Iconify
                    icon={showEvolution ? 'material-symbols:percent-rounded' : 'material-symbols:tag-rounded'}
                    width={16}
                    sx={{ color: 'inherit' }}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: '16.8px',
                      color: 'inherit',
                      whiteSpace: 'nowrap',
                      mx: '2px',
                    }}
                  >
                    evolution
                  </Typography>
                  <Iconify icon="mi:switch" width={16} sx={{ color: 'inherit' }} />
                </Box>
              </Tooltip>
            )}
            <Tooltip title="Show/hide columns" placement="top">
              <IconButton
                ref={columnsBtnRef}
                size="small"
                sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
                onMouseDown={() => {
                  // Capturé avant que la ClickAwayListener ne referme le panneau.
                  const panel = gridApiRef.current?.state?.preferencePanel;
                  columnsWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.columns);
                }}
                onClick={() => {
                  // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                  if (columnsWasOpenRef.current) {
                    gridApiRef.current?.hidePreferences?.();
                    return;
                  }
                  setPanelAnchor(columnsBtnRef.current);
                  gridApiRef.current?.showPreferences?.(GridPreferencePanelsValue.columns);
                }}
              >
                <Iconify icon="mingcute:calendar-week-line" width={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filters" placement="top">
              <IconButton
                ref={filterBtnRef}
                size="small"
                sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
                onMouseDown={() => {
                  // Capturé avant que la ClickAwayListener ne referme le panneau.
                  const panel = gridApiRef.current?.state?.preferencePanel;
                  filterWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.filters);
                }}
                onClick={() => {
                  // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                  if (filterWasOpenRef.current) {
                    gridApiRef.current?.hidePreferences?.();
                    return;
                  }
                  setPanelAnchor(filterBtnRef.current);
                  gridApiRef.current?.showFilterPanel?.();
                }}
              >
                <Iconify icon="ic:round-filter-list" width={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export" placement="top">
              <IconButton
                size="small"
                sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
                onClick={() => {
                  if (queryID) handleExport();
                }}
              >
                <Iconify icon="lets-icons:export" width={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box>
          {/* Le squelette ne couvre QUE le tout premier chargement (aucune donnée encore). Un refetch
            d'arrière-plan (filtre, tri, pagination…) NE DOIT PAS démonter la grille : cela détruirait
            son état interne (colonnes ajoutées/retirées) et le réinitialiserait aux défauts. On garde
            donc la grille montée et on signale le chargement via son overlay (`loading`). */}
          {!tableData && !tableDataError ? (
            <Skeleton
              width={'100%'}
              height={GRID_HEIGHT}
              style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}
              variant="rectangular"
              animation={'wave'}
            >
              <Iconify py={'auto'} icon={'eva:checkmark-circle-2-fill'} color={'success.main'} />
              <Typography color={'success.main'}>Active</Typography>
            </Skeleton>
          ) : (
            tableData && (
              <Box height={'auto'}>
                <GenericDataGrid
                  apiRef={gridApiRef}
                  loading={isFetchingTableData}
                  hideToolbar
                  pinnedColumns={gridPinnedColumns}
                  defaultVisibleFields={GRID_DEFAULT_METRICS}
                  columnOrder={gridColumnOrder}
                  metricBarTypes={GRID_BAR_TYPES}
                  columnMinWidths={exploreColumnMinWidths(!!filterStore?.compare)}
                  columnLabelOverrides={{
                    total_brand_traffic: 'Brand Traffic',
                    [dateField]: 'Date',
                  }}
                  brandTrafficMode="organic-search-shopping"
                  persistColumnVisibility
                  dimensionFilterField={dimensionFilterField}
                  inlineCompare
                  separateDiffFields={['brand_searches']}
                  evolutionMode={showEvolution}
                  gridHeight={fitsWithoutCap ? undefined : GRID_HEIGHT}
                  columnHeaderHeight={36}
                  rowHeight={44}
                  leftAlign
                  showDimensionFlags
                  data={tableData as any}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  paginationMode="server"
                  rowCount={total_rows}
                  sortModel={sortModel}
                  onSortModelChange={(model) => {
                    if (model.length === 0) {
                      setSortModel(defaultSortModelForTab);
                    } else {
                      setSortModel(model as GridSortModel);
                    }
                  }}
                  filterModel={filters}
                  onFilterModelChange={setFilterModel}
                  onVisibilityModelChange={setVisibilityModel}
                  visibilityModel={visibilityModel}
                  onExport={handleExport}
                  panelAnchorEl={panelAnchor}
                />
              </Box>
            )
          )}
        </Box>
      </Paper>

      <ExploreAnalysisChartModal
        open={showAnalysisChart}
        onClose={() => setShowAnalysisChart(false)}
      />
    </>
  );
}